import React, { useEffect, useMemo, useState } from 'react';
import { getCommerceContext } from '../../services/purchaseProgramApi';

function parseProgramScope(assignments = []) {
  const siteKeys = assignments
    .filter((a) => a.entity_type === 'ship_to' && a.entity_key)
    .map((a) => String(a.entity_key));
  const customerKeys = assignments
    .filter((a) => a.entity_type === 'customer' && a.entity_key && a.entity_key !== 'self')
    .map((a) => String(a.entity_key));
  return { siteKeys, customerKeys };
}

/** Programs apply to all sites (or a subset) and all customers or named customers; filters narrow the list. */
function programMatchesScopeFilters(program, siteId, customerId) {
  const { siteKeys, customerKeys } = parseProgramScope(program.assignments || []);
  if (siteId) {
    if (siteKeys.length > 0 && !siteKeys.includes(siteId)) return false;
  }
  if (customerId) {
    if (customerKeys.length > 0 && !customerKeys.includes(customerId)) return false;
  }
  return true;
}

function StatusBadge({ isActive }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '3px 10px', borderRadius: 99, fontSize: 12, fontWeight: 600,
      background: isActive ? '#d1fae5' : '#f3f4f6',
      color: isActive ? '#057a55' : '#6b7280',
    }}>
      <i className={`bi ${isActive ? 'bi-check-circle-fill' : 'bi-pause-circle'}`} aria-hidden="true" style={{ fontSize: 11 }} />
      {isActive ? 'Active' : 'Inactive'}
    </span>
  );
}

function BudgetMeter({ used, limit }) {
  if (limit == null) return <span style={{ fontSize: 12, color: '#6b7280' }}>No limit</span>;
  const pct = Math.min(100, Math.round((used / limit) * 100));
  const color = pct >= 90 ? '#c81e1e' : pct >= 70 ? '#b45309' : '#057a55';
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 3 }}>
        <span style={{ color: '#6b7280' }}>${used?.toLocaleString(undefined, { maximumFractionDigits: 0 })} used</span>
        <span style={{ color: '#374151', fontWeight: 600 }}>{pct}%</span>
      </div>
      <div style={{ height: 6, background: '#e5e7eb', borderRadius: 99, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 99, transition: 'width .4s' }} aria-hidden="true" />
      </div>
      <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>of ${limit?.toLocaleString(undefined, { maximumFractionDigits: 0 })} limit</div>
    </div>
  );
}

// ─── Scope badge resolver ────────────────────────────────────────────────────
function ScopeInfo({ assignments = [] }) {
  const [names, setNames] = useState({});  // { id: name }

  const customerIds = assignments.filter(a => a.entity_type === 'customer' && a.entity_key !== 'self').map(a => a.entity_key);
  const siteIds = assignments.filter(a => a.entity_type === 'ship_to').map(a => a.entity_key);

  useEffect(() => {
    if (customerIds.length === 0 && siteIds.length === 0) return;
    const allIds = [...new Set([...customerIds, ...siteIds])];
    // Try to resolve names from CI — fetch sites first since we need site_id for customers
    getCommerceContext().then(data => {
      const map = {};
      (data?.sites || []).forEach(s => { map[s.id] = s.name; });
      setNames(prev => ({ ...prev, ...map }));
      // For each site in siteIds, also fetch customers
      const siteMatches = (data?.sites || []).filter(s => siteIds.includes(s.id));
      siteMatches.forEach(site => {
        getCommerceContext(site.id).then(cd => {
          const m = {};
          (cd?.customers || []).forEach(c => { m[c.id] = `${c.name}${c.erp_customer_id ? ` (${c.erp_customer_id})` : ''}`; });
          setNames(prev => ({ ...prev, ...m }));
        }).catch(() => {});
      });
      // Also try to resolve customer IDs directly from any site
      if (customerIds.length > 0 && siteMatches.length === 0 && data?.sites?.length > 0) {
        data.sites.forEach(site => {
          getCommerceContext(site.id).then(cd => {
            const m = {};
            (cd?.customers || []).forEach(c => { m[c.id] = `${c.name}${c.erp_customer_id ? ` (${c.erp_customer_id})` : ''}`; });
            setNames(prev => ({ ...prev, ...m }));
          }).catch(() => {});
        });
      }
    }).catch(() => {});
  }, [assignments.map(a => a.entity_key).join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  if (customerIds.length === 0 && siteIds.length === 0) {
    return (
      <span style={{ fontSize: 11, color: '#9ca3af' }}>
        <i className="bi bi-globe me-1" />All sites &amp; customers
      </span>
    );
  }

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
      {siteIds.map(id => (
        <span key={id} style={{ fontSize: 11, background: '#e0f2fe', color: '#0369a1', borderRadius: 6, padding: '2px 7px', fontWeight: 600 }}>
          <i className="bi bi-globe me-1" />{names[id] || id}
        </span>
      ))}
      {customerIds.map(id => (
        <span key={id} style={{ fontSize: 11, background: '#ede9fe', color: '#6d28d9', borderRadius: 6, padding: '2px 7px', fontWeight: 600 }}>
          <i className="bi bi-building me-1" />{names[id] || id}
        </span>
      ))}
    </div>
  );
}

export default function ProgramsTab({ programs, loading, onEdit, onDelete, onAnalytics, onCreateNew }) {
  const [filterSiteId, setFilterSiteId] = useState('');
  const [filterCustomerId, setFilterCustomerId] = useState('');
  const [sitesOptions, setSitesOptions] = useState([]);
  const [customersOptions, setCustomersOptions] = useState([]);
  const [loadingFilters, setLoadingFilters] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoadingFilters(true);
    getCommerceContext()
      .then((data) => {
        if (!cancelled) setSitesOptions(data?.sites || []);
      })
      .catch(() => {
        if (!cancelled) setSitesOptions([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingFilters(false);
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!sitesOptions.length) {
      setCustomersOptions([]);
      return;
    }
    let cancelled = false;
    Promise.all(
      sitesOptions.map((s) => getCommerceContext(s.id).then((d) => d?.customers || []).catch(() => [])),
    ).then((lists) => {
      if (cancelled) return;
      const byId = new Map();
      lists.flat().forEach((c) => {
        if (c?.id && !byId.has(c.id)) byId.set(c.id, c);
      });
      setCustomersOptions(
        [...byId.values()].sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' })),
      );
    });
    return () => { cancelled = true; };
  }, [sitesOptions]);

  const filteredPrograms = useMemo(
    () => programs.filter((p) => programMatchesScopeFilters(p, filterSiteId, filterCustomerId)),
    [programs, filterSiteId, filterCustomerId],
  );

  const clearFilters = () => {
    setFilterSiteId('');
    setFilterCustomerId('');
  };

  if (loading) {
    return (
      <div className="row g-3">
        {[1, 2, 3].map(i => (
          <div key={i} className="col-xl-4 col-md-6">
            <div className="card h-100" style={{ borderRadius: 10 }}>
              <div className="card-body">
                <div className="placeholder-glow">
                  <span className="placeholder col-8 mb-2 d-block" style={{ height: 20, borderRadius: 4 }} />
                  <span className="placeholder col-5 mb-4 d-block" style={{ height: 14, borderRadius: 4 }} />
                  <span className="placeholder col-12 mb-2 d-block" style={{ height: 40, borderRadius: 4 }} />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (programs.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '64px 24px' }}>
        <i className="bi bi-clipboard2-pulse" style={{ fontSize: 56, color: '#d1d5db', display: 'block', marginBottom: 12 }} aria-hidden="true" />
        <h3 style={{ fontSize: 18, fontWeight: 600, color: '#374151', marginBottom: 6 }}>No programs yet</h3>
        <p style={{ color: '#6b7280', marginBottom: 20 }}>Create your first purchase program to get started.</p>
        <button className="btn btn-primary" onClick={onCreateNew} style={{ background: '#1a56db', borderColor: '#1a56db' }}>
          <i className="bi bi-plus-lg me-1" aria-hidden="true" />Create Program
        </button>
      </div>
    );
  }

  return (
    <div className="container-fluid px-0">
      <div
        className="card mb-3 border-0 shadow-sm"
        style={{ borderRadius: 12, border: '1px solid #e5e7eb' }}
      >
        <div className="card-body py-3 px-3 px-md-4">
          <div className="row g-2 g-md-3 align-items-end">
            <div className="col-12 col-md-auto flex-grow-1 flex-md-grow-0">
              <label htmlFor="pp-filter-site" className="form-label small fw-semibold text-secondary mb-1">
                <i className="bi bi-globe me-1" aria-hidden="true" />
                Site
              </label>
              <select
                id="pp-filter-site"
                className="form-select form-select-sm"
                value={filterSiteId}
                onChange={(e) => setFilterSiteId(e.target.value)}
                disabled={loadingFilters}
                aria-label="Filter programs by site"
              >
                <option value="">All sites</option>
                {sitesOptions.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name || s.id}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-12 col-md-auto flex-grow-1 flex-md-grow-0">
              <label htmlFor="pp-filter-customer" className="form-label small fw-semibold text-secondary mb-1">
                <i className="bi bi-building me-1" aria-hidden="true" />
                Customer
              </label>
              <select
                id="pp-filter-customer"
                className="form-select form-select-sm"
                value={filterCustomerId}
                onChange={(e) => setFilterCustomerId(e.target.value)}
                disabled={loadingFilters || customersOptions.length === 0}
                aria-label="Filter programs by customer"
              >
                <option value="">All customers</option>
                {customersOptions.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name || c.id}
                    {c.erp_customer_id ? ` (${c.erp_customer_id})` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-12 col-md-auto">
              {(filterSiteId || filterCustomerId) ? (
                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={clearFilters}>
                  Clear filters
                </button>
              ) : null}
            </div>
          </div>
          {(filterSiteId || filterCustomerId) && (
            <p className="small text-muted mb-0 mt-2">
              Showing {filteredPrograms.length} of {programs.length} program{programs.length !== 1 ? 's' : ''}.
              Programs that apply to all sites or all customers still appear when they match the other filter.
            </p>
          )}
        </div>
      </div>

      {filteredPrograms.length === 0 ? (
        <div className="text-center py-5 px-3">
          <i className="bi bi-funnel" style={{ fontSize: 48, color: '#d1d5db' }} aria-hidden="true" />
          <h3 className="h6 fw-semibold text-secondary mt-3 mb-2">No programs match these filters</h3>
          <p className="text-muted small mb-3">Try changing the site or customer, or clear filters to see every program.</p>
          <button type="button" className="btn btn-sm btn-outline-primary" onClick={clearFilters}>
            Clear filters
          </button>
        </div>
      ) : (
    <div className="row g-3">
      {filteredPrograms.map(prog => {
        const usage = prog.amount_used ?? 0;
        const totalPurchases = prog.purchase_count;
        const tagCount = prog.tags?.length || 0;
        return (
          <div key={prog.id} className="col-xl-4 col-md-6">
            <div className="card h-100" style={{ borderRadius: 10, border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(0,0,0,.06)', transition: 'box-shadow .15s' }}
              onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,.1)'}
              onMouseLeave={e => e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,.06)'}
            >
              <div className="card-body d-flex flex-column" style={{ padding: '18px 20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                  <StatusBadge isActive={prog.is_active} />
                  <span style={{ fontSize: 11, color: '#9ca3af' }}>#{prog.id}</span>
                </div>
                <h3 style={{ fontSize: 16, fontWeight: 700, color: '#111827', marginBottom: 4 }}>{prog.name}</h3>
                {prog.description && (
                  <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 6, lineHeight: 1.5 }}>
                    {prog.description.slice(0, 80)}{prog.description.length > 80 ? '…' : ''}
                  </p>
                )}

                <div style={{ marginBottom: 10 }}>
                  <ScopeInfo assignments={prog.assignments || []} />
                </div>

                <div style={{ display: 'flex', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 12, color: '#374151', display: 'flex', alignItems: 'center', gap: 4 }}>
                    <i className="bi bi-calendar3" aria-hidden="true" style={{ color: '#6b7280' }} />
                    {prog.start_date} {prog.end_date ? `→ ${prog.end_date}` : '(open)'}
                  </span>
                  {tagCount > 0 && (
                    <span style={{ fontSize: 12, color: '#6366f1', display: 'flex', alignItems: 'center', gap: 4 }}>
                      <i className="bi bi-tags" aria-hidden="true" />
                      {tagCount} tag{tagCount !== 1 ? 's' : ''}
                    </span>
                  )}
                  {(prog.product_list_ids?.length > 0) ? (
                    <span style={{ fontSize: 12, color: '#0369a1', display: 'flex', alignItems: 'center', gap: 4 }}>
                      <i className="bi bi-box-seam" aria-hidden="true" />
                      {prog.product_list_ids.length} list{prog.product_list_ids.length !== 1 ? 's' : ''}
                    </span>
                  ) : (
                    <span style={{ fontSize: 12, color: '#b45309', background: '#fef9c3', border: '1px solid #fbbf24', borderRadius: 6, padding: '1px 8px', display: 'flex', alignItems: 'center', gap: 4 }}
                      title="No product lists assigned — this program will show the full catalog. Edit the program to assign product lists.">
                      <i className="bi bi-exclamation-triangle-fill" aria-hidden="true" />
                      No product lists — full catalog
                    </span>
                  )}
                  {prog.limits_missing && (
                    <span style={{ fontSize: 12, color: '#dc2626', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 6, padding: '1px 8px', display: 'flex', alignItems: 'center', gap: 4 }}
                      title="No limits configured at the program level or per-list level. Users will have unrestricted purchasing. Edit the program to add qty or dollar limits.">
                      <i className="bi bi-shield-exclamation" aria-hidden="true" />
                      No limits set
                    </span>
                  )}
                </div>

                <div style={{ marginBottom: 16 }}>
                  <BudgetMeter used={usage} limit={prog.amount_limit} />
                </div>

                <div className="d-flex gap-2 mt-auto">
                  <button
                    className="btn btn-sm btn-outline-primary"
                    style={{ flex: 1, fontSize: 13 }}
                    onClick={() => onEdit(prog)}
                    aria-label={`Edit ${prog.name}`}
                  >
                    <i className="bi bi-pencil me-1" aria-hidden="true" />Edit
                  </button>
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    style={{ fontSize: 13, width: 34 }}
                    onClick={() => onAnalytics(prog)}
                    aria-label={`View analytics for ${prog.name}`}
                    title="Analytics"
                  >
                    <i className="bi bi-bar-chart-line" aria-hidden="true" />
                  </button>
                  <button
                    className="btn btn-sm btn-outline-danger"
                    style={{ fontSize: 13, width: 34 }}
                    onClick={() => onDelete(prog)}
                    aria-label={`Delete ${prog.name}`}
                    title="Delete"
                  >
                    <i className="bi bi-trash3" aria-hidden="true" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
      )}
    </div>
  );
}
